import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './SignUp.css'; // Import the CSS file for styling
import signupImage from '../../assets/images/purple-signup-floatin-text-bangers.svg'; // Import the SVG

// Utility function to generate a random user ID
const generateUserId = () => {
  const characters = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let userId = '';
  for (let i = 0; i < 6; i++) {
    userId += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return userId;
};

const SignUp = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    email: '',
    invitationCode: ''
  });
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState(''); // Add state for password error
  const navigate = useNavigate();

  // Add API_BASE_URL from environment variables
  const API_BASE_URL = process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_PRODUCTION_API_BASE_URL
    : process.env.REACT_APP_API_BASE_URL;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear password error when either password field changes
    if (name === 'password' || name === 'confirmPassword') {
      setPasswordError('');
    }
  };

  // Add new handler for password confirmation
  const handlePasswordBlur = () => {
    if (formData.password && formData.confirmPassword) {
      if (formData.password !== formData.confirmPassword) {
        setPasswordError('Passwords do not match');
      } else {
        setPasswordError('');
      }
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setMessage('');
    setIsSubmitting(true);

    // Check if there's a password error before proceeding
    if (passwordError) {
      setMessage('Please fix the password error before submitting.');
      setIsSubmitting(false);
      return;
    }

    // Add invitationCode to validation
    if (!formData.username || !formData.password || !formData.firstName || !formData.lastName || !formData.email || !formData.invitationCode) {
      setMessage('Please fill in all fields, including the invitation code.');
      setIsSubmitting(false);
      return;
    }

    const userId = generateUserId();
    // Include invitationCode in the payload sent to the backend
    const payload = {
      username: formData.username,
      password: formData.password,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      userId: userId,
      invitationCode: formData.invitationCode
    };

    try {
      // Use API_BASE_URL and add /api prefix
      // Also, the backend route is /api/users/register now
      const response = await fetch(`${API_BASE_URL}/api/users/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload), // Send the updated payload
      });

      if (response.ok) {
        setMessage('Sign up successful! Redirecting to login...');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        const errorData = await response.json();
        // Display the specific error from the backend (e.g., invalid code)
        setMessage(`Sign up failed: ${errorData.error || 'Please try again.'}`);
        setIsSubmitting(false);
      }
    } catch (error) {
      setMessage('An error occurred during sign up.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="signup-page">
      <h1 className="signup-title">
        <img src={signupImage} alt="Sign Up" />
      </h1>
      <div className="signup-form-container">
        <form onSubmit={handleSignUp}>
          <div className="form-group">
            <label htmlFor="username">Username:</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password:</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password:</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              onBlur={handlePasswordBlur}
              required
            />
            {passwordError && (
              <div className="password-error">
                {passwordError}
              </div>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="firstName">First Name:</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="lastName">Last Name:</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email:</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="invitationCode">Invitation Code:</label>
            <input
              type="text"
              id="invitationCode"
              name="invitationCode"
              value={formData.invitationCode}
              onChange={handleChange}
              required
            />
          </div>
          <button type="submit" className="signup-button" disabled={isSubmitting}>
            Sign Up
          </button>
        </form>
        {message && (
          <div className={`message ${message.startsWith('Sign up successful') ? 'success' : 'error'}`}>
            {message}
          </div>
        )}
        <div className="login-prompt">
          Already have an account? <Link to="/login">Login</Link>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
